import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faBellSlash,
  faBuilding,
  faCheck,
  faCheckCircle,
  faClock,
  faDesktop,
  faEnvelope,
  faExclamationTriangle,
  faHourglassEnd,
  faHourglassStart,
  faInfoCircle,
  faMobileAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import SubtleBadge from 'components/common/SubtleBadge';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import {
  errorMessage,
  webKey,
  fetchPreferences,
  saveWebSubscriptions,
  saveMobileSubscription
} from './notificationPreferencesApi';

const DIRECTION_ICON = {
  before: faHourglassStart,
  at_dose: faClock,
  after: faHourglassEnd
};

// Brand colours straight from the theme variables: .bg-primary / .text-primary
// read --pelmeds-blue-rgb, which the light theme sets to a hex, so they don't show.
const ICON_ON = { backgroundColor: 'var(--pelmeds-blue)', color: '#fff' };
const CHIP_ON = {
  backgroundColor: 'var(--falcon-primary-bg-subtle)',
  color: 'var(--falcon-primary-text-emphasis)',
  borderColor: 'var(--falcon-primary-text-emphasis)'
};

const readStoredFacilities = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('facilitiesData') || '[]');
    return Object.values(stored || {}).map(f => ({
      id: String(f.value),
      name: f.key
    }));
  } catch (err) {
    return [];
  }
};

/* ------------------------------------------------------------------ */
/* Presentational pieces                                               */
/* ------------------------------------------------------------------ */

const ChannelCard = ({ icon, color, title, subtitle, badge, children }) => (
  <Card className="h-100 border border-200 shadow-none overflow-hidden">
    <Card.Header className="bg-body-tertiary border-bottom border-200 py-3">
      <div className="d-flex align-items-center gap-3">
        <span
          className={`d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-${color}-subtle text-${color}`}
          style={{ width: 40, height: 40 }}
        >
          <FontAwesomeIcon icon={icon} />
        </span>
        <div className="flex-1" style={{ minWidth: 0 }}>
          <h6 className="mb-0 fw-bold text-900 fs-8">{title}</h6>
          <p className="mb-0 fs-10 text-600">{subtitle}</p>
        </div>
        {badge}
      </div>
    </Card.Header>
    <Card.Body className="p-0">{children}</Card.Body>
  </Card>
);

ChannelCard.propTypes = {
  icon: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  badge: PropTypes.node,
  children: PropTypes.node
};

const OnBadge = ({ on, total }) => (
  <SubtleBadge
    bg={on > 0 ? 'success' : 'secondary'}
    pill
    className="flex-shrink-0 fs-10"
  >
    {total === undefined ? `${on} on` : `${on} of ${total} on`}
  </SubtleBadge>
);

OnBadge.propTypes = {
  on: PropTypes.number.isRequired,
  total: PropTypes.number
};

/* One notification with its on/off switch. The whole label toggles it. */
const PreferenceRow = ({
  id,
  title,
  icon,
  checked,
  pending,
  disabled,
  onChange,
  last,
  children
}) => (
  <div className={`px-3 py-2 ${last ? '' : 'border-bottom border-200'}`}>
    <div className="d-flex align-items-center gap-3">
      {icon && (
        <span
          className={`d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
            checked ? '' : 'bg-body-tertiary text-500 border border-200'
          }`}
          style={{
            width: 32,
            height: 32,
            transition: 'all .15s ease-in-out',
            ...(checked ? ICON_ON : {})
          }}
        >
          <FontAwesomeIcon icon={icon} className="fs-10" />
        </span>
      )}
      <label
        htmlFor={id}
        className="flex-1 mb-0 py-1"
        style={{ cursor: disabled ? 'default' : 'pointer', minWidth: 0 }}
      >
        <span
          className={`d-block fs-9 ${
            checked ? 'fw-semibold text-900' : 'text-700'
          }`}
        >
          {title}
        </span>
      </label>
      {pending && (
        <Spinner animation="border" size="sm" variant="primary" role="status" />
      )}
      <Form.Check
        type="switch"
        id={id}
        className="mb-0"
        style={{ fontSize: '1.15rem' }}
        checked={checked}
        disabled={disabled || pending}
        onChange={e => onChange(e.target.checked)}
      />
    </div>
    {children}
  </div>
);

PreferenceRow.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.object,
  checked: PropTypes.bool,
  pending: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  last: PropTypes.bool,
  children: PropTypes.node
};

const ChannelChip = ({ icon, label, active, disabled, onClick }) => (
  <Button
    size="sm"
    variant={active ? 'light' : 'outline-secondary'}
    className="rounded-pill py-0 px-2 fs-10 fw-semibold"
    style={active ? CHIP_ON : undefined}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
  >
    <FontAwesomeIcon icon={icon} className="me-1" />
    {label}
    {active && <FontAwesomeIcon icon={faCheck} className="ms-1" />}
  </Button>
);

ChannelChip.propTypes = {
  icon: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

const EmptyState = ({ icon, children }) => (
  <div className="text-center px-3 py-5">
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-tertiary text-400 border border-200 mb-3"
      style={{ width: 48, height: 48 }}
    >
      <FontAwesomeIcon icon={icon} />
    </span>
    <p className="fs-9 text-600 mb-0 mx-auto" style={{ maxWidth: 320 }}>
      {children}
    </p>
  </div>
);

EmptyState.propTypes = {
  icon: PropTypes.object.isRequired,
  children: PropTypes.node
};

/* ------------------------------------------------------------------ */
/* Notification preferences                                            */
/* ------------------------------------------------------------------ */

const UserNotifcation = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [webTypes, setWebTypes] = useState([]);
  const [webSubs, setWebSubs] = useState(() => new Set());
  const [mobileFacilities, setMobileFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [pending, setPending] = useState({});
  const [savedOnce, setSavedOnce] = useState(false);

  const storedFacilities = useMemo(readStoredFacilities, []);

  const load = useCallback(async ({ quiet } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError('');
    try {
      const prefs = await fetchPreferences();
      setWebTypes(prefs.webTypes);
      setWebSubs(prefs.webSubscriptions);
      setMobileFacilities(prefs.mobileFacilities);
    } catch (err) {
      setLoadError(errorMessage(err, 'Please try again in a moment.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Every facility the user can pick: their linked facilities, the ones they
  // can get mobile alerts at, and any they already have web alerts for.
  const facilities = useMemo(() => {
    const byId = new Map();
    const add = (id, name) => {
      if (id === undefined || id === null) return;
      const key = String(id);
      if (!byId.has(key) || (!byId.get(key).name && name))
        byId.set(key, { id: key, name });
    };
    storedFacilities.forEach(f => add(f.id, f.name));
    mobileFacilities.forEach(f => add(f.id, f.name));
    webSubs.forEach(key => add(key.split(':')[0]));
    return [...byId.values()]
      .map(f => ({ ...f, name: f.name || `Facility #${f.id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [storedFacilities, mobileFacilities, webSubs]);

  useEffect(() => {
    if (facilities.length && !facilities.some(f => f.id === facilityId)) {
      setFacilityId(facilities[0].id);
    }
  }, [facilities, facilityId]);

  const counts = useMemo(() => {
    const webIds = new Set(webTypes.map(t => String(t.id)));
    const out = {};
    facilities.forEach(f => {
      out[f.id] = { web: 0, mobile: 0 };
    });
    webSubs.forEach(key => {
      const [fid, nid] = key.split(':');
      if (out[fid] && webIds.has(nid)) out[fid].web += 1;
    });
    mobileFacilities.forEach(f => {
      if (!out[f.id]) return;
      out[f.id].mobile = f.types.reduce(
        (n, t) => n + t.items.filter(i => i.subscribed).length,
        0
      );
    });
    return out;
  }, [facilities, webTypes, webSubs, mobileFacilities]);

  const facilityOptions = facilities.map(f => ({ value: f.id, label: f.name }));
  const selectedOption =
    facilityOptions.find(o => o.value === facilityId) || null;
  const mobileFacility = mobileFacilities.find(f => f.id === facilityId);
  const saving = Object.keys(pending).length > 0;

  const markPending = (keys, on) =>
    setPending(prev => {
      const next = { ...prev };
      keys.forEach(k => {
        if (on) next[k] = true;
        else delete next[k];
      });
      return next;
    });

  /* -------------------- web -------------------- */

  const setWeb = (fid, ids, active) =>
    setWebSubs(prev => {
      const next = new Set(prev);
      ids.forEach(id =>
        active ? next.add(webKey(fid, id)) : next.delete(webKey(fid, id))
      );
      return next;
    });

  const toggleWeb = async (ids, active) => {
    if (ids.length === 0) return;
    const fid = facilityId;
    const keys = ids.map(id => `web:${webKey(fid, id)}`);
    setWeb(fid, ids, active);
    markPending(keys, true);
    try {
      await saveWebSubscriptions(fid, ids, active);
      setSavedOnce(true);
    } catch (err) {
      setWeb(fid, ids, !active);
      toast.error(
        errorMessage(err, "Couldn't update your web notifications."),
        {
          theme: 'colored'
        }
      );
    } finally {
      markPending(keys, false);
    }
  };

  /* -------------------- mobile -------------------- */

  const setMobileItem = (fid, itemKey, patch) =>
    setMobileFacilities(prev =>
      prev.map(f =>
        f.id !== fid
          ? f
          : {
              ...f,
              types: f.types.map(t => ({
                ...t,
                items: t.items.map(i =>
                  i.key === itemKey ? { ...i, ...patch } : i
                )
              }))
            }
      )
    );

  const saveMobile = async (item, next) => {
    const fid = facilityId;
    const key = `mobile:${fid}:${item.key}`;
    // Subscribing needs at least one way to receive it.
    const wanted =
      next.subscribed && !next.push && !next.email
        ? { ...next, push: true }
        : next;
    setMobileItem(fid, item.key, wanted);
    markPending([key], true);
    try {
      await saveMobileSubscription(fid, item, wanted);
      setSavedOnce(true);
    } catch (err) {
      setMobileItem(fid, item.key, {
        subscribed: item.subscribed,
        push: item.push,
        email: item.email
      });
      toast.error(
        errorMessage(err, "Couldn't update this mobile notification."),
        {
          theme: 'colored'
        }
      );
      // A reminder disabled or removed by the admin meanwhile: show what's there now.
      if ([404, 409].includes(err?.response?.status)) load({ quiet: true });
    } finally {
      markPending([key], false);
    }
  };

  const toggleChannel = (item, channel) => {
    const next = {
      subscribed: true,
      push: item.push,
      email: item.email,
      [channel]: !item[channel]
    };
    if (!next.push && !next.email) {
      toast.info('Keep at least one way to receive this notification.', {
        theme: 'colored'
      });
      return;
    }
    saveMobile(item, next);
  };

  /* -------------------- render -------------------- */

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" variant="primary" role="status" />
        <p className="mt-3 text-600 fs-10 mb-0">
          Loading your notification preferences...
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-3 p-md-4">
        <Alert
          variant="danger"
          className="d-flex align-items-start border-0 rounded-3 mb-0"
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="me-3 mt-1 fs-9"
          />
          <div className="flex-1">
            <div className="fw-bold fs-10">
              We couldn&apos;t load your notification preferences
            </div>
            <div className="fs-10">{loadError}</div>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3 rounded-pill px-3"
            onClick={() => load()}
          >
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const webOn = webTypes.filter(t => webSubs.has(webKey(facilityId, t.id)));
  const webOff = webTypes.filter(t => !webSubs.has(webKey(facilityId, t.id)));

  return (
    <div className="p-3 p-md-4">
      {/* Heading + autosave status */}
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h5 className="mb-1 fw-bold text-900">
            <FontAwesomeIcon icon={faBell} className="text-primary me-2" />
            Notification preferences
          </h5>
          <p className="text-600 mb-0 fs-9">
            Choose the alerts you want for each facility: on the web, on your
            phone, or both.
          </p>
        </div>
        <div className="fs-9 pt-1" aria-live="polite">
          {saving ? (
            <span className="text-600">
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </span>
          ) : savedOnce ? (
            <span className="text-success fw-semibold">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              All changes saved
            </span>
          ) : (
            <span className="text-500">
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
              Changes save automatically
            </span>
          )}
        </div>
      </div>

      {facilities.length === 0 ? (
        <Card className="border border-200 shadow-none">
          <EmptyState icon={faBuilding}>
            You aren&apos;t linked to any facility yet, so there are no
            notifications to choose from.
          </EmptyState>
        </Card>
      ) : (
        <>
          {/* Facility picker */}
          <div className="p-3 mb-4 rounded-3 border border-200 bg-body-tertiary">
            <Row className="align-items-center g-2">
              <Col xs={12} md="auto">
                <Form.Label
                  htmlFor="pref-facility-select"
                  className="fw-semibold text-900 mb-0"
                >
                  <FontAwesomeIcon
                    icon={faBuilding}
                    className="text-primary me-2"
                  />
                  Facility
                </Form.Label>
              </Col>
              <Col xs={12} md={7} lg={5}>
                <ThemedSelect
                  inputId="pref-facility-select"
                  options={facilityOptions}
                  value={selectedOption}
                  onChange={opt => opt && setFacilityId(opt.value)}
                  isSearchable={facilityOptions.length > 6}
                  formatOptionLabel={(opt, { context }) =>
                    context === 'menu' ? (
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <span>{opt.label}</span>
                        <span className="fs-10 text-500 text-nowrap">
                          {counts[opt.value]?.web || 0} web ·{' '}
                          {counts[opt.value]?.mobile || 0} mobile
                        </span>
                      </div>
                    ) : (
                      opt.label
                    )
                  }
                />
              </Col>
            </Row>
          </div>

          <Row className="g-4">
            {/* Web */}
            <Col xs={12} xl={6}>
              <ChannelCard
                icon={faDesktop}
                color="info"
                title="Web"
                subtitle="Alerts in MapYourMeds when you're signed in on the web."
                badge={<OnBadge on={webOn.length} total={webTypes.length} />}
              >
                {webTypes.length === 0 ? (
                  <EmptyState icon={faBellSlash}>
                    There are no web notifications available for you.
                  </EmptyState>
                ) : (
                  <>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 py-2 border-bottom border-200">
                      <span className="fs-10 text-600">
                        Flip a switch to turn an alert on or off.
                      </span>
                      <div className="d-flex gap-2">
                        <Button
                          variant="falcon-default"
                          size="sm"
                          className="fw-semibold"
                          disabled={webOff.length === 0}
                          onClick={() =>
                            toggleWeb(
                              webOff.map(t => t.id),
                              true
                            )
                          }
                        >
                          Turn all on
                        </Button>
                        <Button
                          variant="falcon-default"
                          size="sm"
                          className="fw-semibold"
                          disabled={webOn.length === 0}
                          onClick={() =>
                            toggleWeb(
                              webOn.map(t => t.id),
                              false
                            )
                          }
                        >
                          Turn all off
                        </Button>
                      </div>
                    </div>
                    {webTypes.map((type, idx) => (
                      <PreferenceRow
                        key={type.id}
                        id={`web-${facilityId}-${type.id}`}
                        title={type.name}
                        checked={webSubs.has(webKey(facilityId, type.id))}
                        pending={Boolean(
                          pending[`web:${webKey(facilityId, type.id)}`]
                        )}
                        onChange={on => toggleWeb([type.id], on)}
                        last={idx === webTypes.length - 1}
                      />
                    ))}
                  </>
                )}
              </ChannelCard>
            </Col>

            {/* Mobile */}
            <Col xs={12} xl={6}>
              <ChannelCard
                icon={faMobileAlt}
                color="success"
                title="Mobile app"
                subtitle="Push notifications on your phone, or by email if you prefer."
                badge={
                  mobileFacility && (
                    <OnBadge on={counts[facilityId]?.mobile || 0} />
                  )
                }
              >
                {!mobileFacility || mobileFacility.types.length === 0 ? (
                  <EmptyState icon={faMobileAlt}>
                    Mobile notifications aren&apos;t available for you at this
                    facility.
                  </EmptyState>
                ) : (
                  <>
                    {mobileFacility.types.some(
                      t => t.configured && t.items.length > 0
                    ) && (
                      <div className="px-3 py-2 border-bottom border-200 fs-10 text-600">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="me-1 text-info"
                        />
                        Reminder times are set by your administrator. Turn on
                        the ones you want, then choose how to get them.
                      </div>
                    )}
                    {mobileFacility.types.map((type, typeIdx) => {
                      const single =
                        type.kind !== 'medpass' && type.items.length === 1;
                      const available =
                        type.configured && type.items.length > 0;
                      const lastType =
                        typeIdx === mobileFacility.types.length - 1;
                      return (
                        <div
                          key={type.id}
                          className={lastType ? '' : 'border-bottom border-200'}
                          style={available ? undefined : { opacity: 0.6 }}
                        >
                          {(!single || !available) && (
                            <div className="d-flex align-items-center justify-content-between gap-2 px-3 pt-3 pb-1">
                              <span className="fs-9 fw-bold text-900">
                                {type.name}
                              </span>
                              {!available && (
                                <SubtleBadge
                                  bg="secondary"
                                  pill
                                  className="fs-10"
                                >
                                  Not set up here
                                </SubtleBadge>
                              )}
                            </div>
                          )}
                          {!available ? (
                            <p className="px-3 pb-3 pt-1 mb-0 fs-10 text-500 fst-italic">
                              Nothing to turn on yet. Your administrator
                              hasn&apos;t set this up at this facility.
                            </p>
                          ) : (
                            type.items.map((item, idx) => {
                              const itemPending = Boolean(
                                pending[`mobile:${facilityId}:${item.key}`]
                              );
                              return (
                                <PreferenceRow
                                  key={item.key}
                                  id={`mobile-${facilityId}-${item.key}`}
                                  title={single ? type.name : item.label}
                                  icon={
                                    DIRECTION_ICON[item.direction] || faBell
                                  }
                                  checked={item.subscribed}
                                  pending={itemPending}
                                  onChange={on =>
                                    saveMobile(item, {
                                      subscribed: on,
                                      push: item.push,
                                      email: item.email
                                    })
                                  }
                                  last={idx === type.items.length - 1}
                                >
                                  {item.subscribed && (
                                    <div
                                      className="d-flex flex-wrap align-items-center gap-2 mt-1 mb-1"
                                      style={{ paddingLeft: 48 }}
                                    >
                                      <span className="fs-10 text-600">
                                        Send by
                                      </span>
                                      <ChannelChip
                                        icon={faMobileAlt}
                                        label="Push"
                                        active={item.push}
                                        disabled={itemPending}
                                        onClick={() =>
                                          toggleChannel(item, 'push')
                                        }
                                      />
                                      <ChannelChip
                                        icon={faEnvelope}
                                        label="Email"
                                        active={item.email}
                                        disabled={itemPending}
                                        onClick={() =>
                                          toggleChannel(item, 'email')
                                        }
                                      />
                                    </div>
                                  )}
                                </PreferenceRow>
                              );
                            })
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </ChannelCard>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default UserNotifcation;
